/**
 * Sync engine — drains the outbox when the device is online.
 *
 * Lifecycle:
 *   1. App boot → OfflineProvider calls SyncEngine.start({ companyId, businessId })
 *   2. Engine listens for `online` events, periodic ticks, and outbox writes
 *   3. On each trigger it pulls the next drainable batch and POSTs each
 *      item to the configured api client
 *   4. On success: markDone
 *      On 4xx (excluding 401/429): markConflict (operator action required)
 *      On 401: skip — auth interceptor handles refresh; will retry next tick
 *      On 5xx / network / 429: markFailed with exponential backoff
 *
 * Tab coordination via BroadcastChannel:
 *   Multiple tabs share the same IndexedDB but should NOT both POST the
 *   same item. We use a BroadcastChannel + a soft "leader lock" so only
 *   one tab actively drains at a time. The leader pings every 5s; if no
 *   ping for 15s, another tab takes over.
 */
import type { AxiosInstance } from "axios";
import {
  getNextDrainable,
  markInFlight,
  markDone,
  markFailed,
  markConflict,
  getStats,
  gc,
  recoverStuck,
  type OutboxItem,
} from "./outbox";
import { onOutboxChanged } from "./outbox";

// ── Configuration ───────────────────────────────────────────────

/** Exponential backoff schedule. After max, plateaus at last value. */
const BACKOFF_SCHEDULE_MS = [
  1_000, // 1s
  5_000, // 5s
  15_000, // 15s
  30_000, // 30s
  60_000, // 1m
  5 * 60_000, // 5m
  15 * 60_000, // 15m
  30 * 60_000, // 30m
];

function backoffFor(attempts: number): number {
  const idx = Math.min(attempts - 1, BACKOFF_SCHEDULE_MS.length - 1);
  return BACKOFF_SCHEDULE_MS[Math.max(idx, 0)];
}

const TICK_INTERVAL_MS = 5_000;
const GC_INTERVAL_MS = 5 * 60_000;
const LEADER_HEARTBEAT_MS = 5_000;
const LEADER_TIMEOUT_MS = 15_000;

// ── Engine state ────────────────────────────────────────────────

export interface SyncEngineConfig {
  api: AxiosInstance;
  companyId: string;
  businessId: string;
  /** Only this user's items are sent (see getNextDrainable). */
  userId?: string | null;
  /** Called whenever the engine ticks (drain attempted). UI can update. */
  onTick?: () => void;
  /** Called after an item reaches the server (refresh lists, stock…). */
  onSynced?: (item: OutboxItem) => void;
}

let activeConfig: SyncEngineConfig | null = null;
let tickTimer: ReturnType<typeof setInterval> | null = null;
let gcTimer: ReturnType<typeof setInterval> | null = null;
let leaderTimer: ReturnType<typeof setInterval> | null = null;
let bc: BroadcastChannel | null = null;
let isLeader = false;
let lastLeaderPing = 0;
let outboxUnsubscribe: (() => void) | null = null;
let isDraining = false;

// ── Tab coordination ────────────────────────────────────────────

const CHANNEL_NAME = "eaze-sync-leader";
const tabId =
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `tab-${Math.random().toString(36).slice(2)}`;

function startLeaderElection() {
  if (typeof BroadcastChannel === "undefined") {
    // Old browsers: assume leader (no coordination needed; multiple tabs
    // will just both try, idempotency on the server handles dups).
    isLeader = true;
    return;
  }

  bc = new BroadcastChannel(CHANNEL_NAME);
  let lastSeenLeaderId: string | null = null;
  let lastSeenLeaderAt = 0;

  bc.onmessage = (e) => {
    const msg = e.data as { type: string; tabId: string; ts: number };
    if (msg.type === "ping") {
      lastSeenLeaderId = msg.tabId;
      lastSeenLeaderAt = msg.ts;
      // If we WERE leader and another tab is also leader, the lower
      // tabId wins. Yield otherwise.
      if (isLeader && msg.tabId < tabId) {
        isLeader = false;
      }
    }
  };

  // Heartbeat loop: claim leadership if no recent ping from anyone else
  leaderTimer = setInterval(() => {
    const now = Date.now();
    if (
      !lastSeenLeaderId ||
      lastSeenLeaderId === tabId ||
      now - lastSeenLeaderAt > LEADER_TIMEOUT_MS
    ) {
      isLeader = true;
    }
    if (isLeader) {
      lastLeaderPing = now;
      bc?.postMessage({ type: "ping", tabId, ts: now });
    }
  }, LEADER_HEARTBEAT_MS);

  // Initial claim
  isLeader = true;
  bc.postMessage({ type: "ping", tabId, ts: Date.now() });
}

function stopLeaderElection() {
  if (bc) {
    bc.close();
    bc = null;
  }
  if (leaderTimer) {
    clearInterval(leaderTimer);
    leaderTimer = null;
  }
  isLeader = false;
}

// ── Core drain loop ─────────────────────────────────────────────

async function tryDrainOne(item: OutboxItem): Promise<void> {
  const config = activeConfig;
  if (!config) return;
  const { api } = config;

  try {
    await markInFlight(item.id);
    const res = await api.request({
      method: item.method,
      url: item.endpoint,
      data: item.payload,
      headers: {
        // Same key = same sale on the server, however late it arrives
        // (also in the body). Must be "Idempotency-Key" — the API reads it
        // and CORS allows it.
        "Idempotency-Key": item.id,
        // The business the item was made in, not whichever is open now.
        "X-Business-Id": item.businessId,
      },
    });
    await markDone(item.id, (res.data as Record<string, unknown>) ?? null);
    config.onSynced?.(item);
  } catch (err) {
    const e = err as {
      response?: { status?: number; data?: unknown };
      message?: string;
      code?: string;
    };
    const status = e.response?.status;
    const detail =
      typeof e.response?.data === "object" && e.response.data !== null
        ? (e.response.data as Record<string, unknown>)
        : null;
    const message =
      (detail?.message as string) ?? (detail?.error as string) ?? e.message ?? "Sync failed";

    if (status === 401) {
      // Auth refresh in flight — leave as in_flight so the next tick
      // doesn't double-attempt. Reset to pending after a short delay.
      setTimeout(() => {
        markFailed(item.id, { message: "Auth refresh in flight" }, 2_000).catch(
          () => {},
        );
      }, 500);
      return;
    }

    if (status === 402 || status === 408 || status === 423) {
      // Subscription lapsed / timeout / locked — not this sale's fault; wait and retry.
      await markFailed(item.id, { message, detail: detail ?? undefined }, 5 * 60_000);
      return;
    }

    if (status === 429) {
      // Rate limited — long backoff
      await markFailed(
        item.id,
        { message: "Rate limited", detail: detail ?? undefined },
        60_000,
      );
      return;
    }

    if (status && status >= 400 && status < 500) {
      // Permanent — operator must resolve
      await markConflict(item.id, {
        message,
        detail: detail ?? undefined,
      });
      return;
    }

    // 5xx, network, undefined → retry with backoff
    const wait = backoffFor((item.attempts || 0) + 1);
    await markFailed(item.id, { message, detail: detail ?? undefined }, wait);
  }
}

async function drainOnce() {
  const config = activeConfig;
  if (!config || isDraining) return;
  if (!navigator.onLine) return; // skip if browser knows we're offline
  if (!isLeader) return; // only leader tab drains

  isDraining = true;
  try {
    const batch = await getNextDrainable(
      config.companyId,
      config.businessId,
      10,
      config.userId,
    );
    // Process sequentially so we don't blast the server with N
    // simultaneous requests (which would defeat backoff)
    for (const item of batch) {
      await tryDrainOne(item);
    }
  } catch (e) {
    console.warn("[sync-engine] drain loop error", e);
  } finally {
    isDraining = false;
    config.onTick?.();
  }
}

// ── Public API ──────────────────────────────────────────────────

export const SyncEngine = {
  start(config: SyncEngineConfig) {
    if (activeConfig) this.stop();
    activeConfig = config;

    startLeaderElection();

    // Periodic tick
    tickTimer = setInterval(drainOnce, TICK_INTERVAL_MS);
    // GC tick
    gcTimer = setInterval(() => {
      gc().catch(() => {});
    }, GC_INTERVAL_MS);
    // Online event → immediate drain
    if (typeof window !== "undefined") {
      window.addEventListener("online", drainOnce);
    }
    // New item enqueued → immediate drain
    outboxUnsubscribe = onOutboxChanged((detail) => {
      if (detail?.action === "enqueue" || detail?.action === "reset") {
        drainOnce();
      }
    });

    // Initial drain (after putting back anything a closed tab left mid-send)
    recoverStuck()
      .catch(() => 0)
      .then(() => drainOnce());
  },

  stop() {
    if (tickTimer) clearInterval(tickTimer);
    if (gcTimer) clearInterval(gcTimer);
    tickTimer = null;
    gcTimer = null;
    if (typeof window !== "undefined") {
      window.removeEventListener("online", drainOnce);
    }
    if (outboxUnsubscribe) {
      outboxUnsubscribe();
      outboxUnsubscribe = null;
    }
    stopLeaderElection();
    activeConfig = null;
  },

  /** Force an immediate drain. Useful for a "Sync now" button. */
  async drainNow() {
    return drainOnce();
  },

  isLeader() {
    return isLeader;
  },

  async stats() {
    if (!activeConfig) return null;
    return getStats(activeConfig.companyId, activeConfig.businessId);
  },
};

export type { OutboxItem };
